import React, { useState } from "react"
import { SpinnerText } from "../ui/Spinner"
import { useSelector, useDispatch } from "react-redux"
import { RootState } from "../../store/store"
import { updateUser } from "../../slices/authSlice"
import { updateMe } from "../../services/userService"
import SectionHeader from "../ui/SectionHeader"
import { FiUser, FiMapPin, FiBriefcase } from "react-icons/fi"

const inputStyle =
  "w-full rounded-lg border border-secondary4 px-4 py-2.5 text-sm focus:border-dantzOrange focus:outline-none focus:ring-2 focus:ring-dantzOrange/20"

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false
}: {
  label: string
  name: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  type?: string
  required?: boolean
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">
        {label}
        {required && " *"}
      </label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        className={inputStyle}
      />
    </div>
  )
}

export default function ProfileForm() {
  const user = useSelector((state: RootState) => state.auth.user)
  const dispatch = useDispatch()

  const [form, setForm] = useState({
    title: user?.title ?? "",
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    phone: user?.phone ?? "",
    street: user?.street ?? "",
    city: user?.city ?? "",
    country: user?.country ?? "",
    company: user?.company ?? "",
    companyStreet: user?.companyStreet ?? "",
    companyCity: user?.companyCity ?? "",
    companyCountry: user?.companyCountry ?? ""
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    setError("")
    setSuccess("")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    setSuccess("")

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("First and last name are required.")
      return
    }
    if (!emailPattern.test(form.email)) {
      setError("Please enter a valid email address.")
      return
    }
    if (!form.street.trim() || !form.city.trim() || !form.country.trim()) {
      setError("Street, city and country are required.")
      return
    }

    setSaving(true)
    try {
      const res = await updateMe(form)
      if (res?.user) {
        dispatch(updateUser(res.user))
      }
      setSuccess("Your profile has been updated.")
    } catch (err: any) {
      setError(
        err?.message || "Your profile could not be updated. Please try again."
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <SectionHeader icon={<FiUser size={20} />} title="Personal information" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Title" name="title" value={form.title} onChange={handleChange} />
          <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} />
          <Field label="First name" name="firstName" value={form.firstName} onChange={handleChange} required />
          <Field label="Last name" name="lastName" value={form.lastName} onChange={handleChange} required />
          <div className="sm:col-span-2">
            <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <SectionHeader icon={<FiMapPin size={20} />} title="Private address" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Street" name="street" value={form.street} onChange={handleChange} required />
          </div>
          <Field label="City" name="city" value={form.city} onChange={handleChange} required />
          <Field label="Country" name="country" value={form.country} onChange={handleChange} required />
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <SectionHeader
          icon={<FiBriefcase size={20} />}
          title="Company address"
          subtitle="Optional."
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Company name" name="company" value={form.company} onChange={handleChange} />
          </div>
          <div className="sm:col-span-2">
            <Field label="Company street" name="companyStreet" value={form.companyStreet} onChange={handleChange} />
          </div>
          <Field label="Company city" name="companyCity" value={form.companyCity} onChange={handleChange} />
          <Field label="Company country" name="companyCountry" value={form.companyCountry} onChange={handleChange} />
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
          {success}
        </div>
      )}

      <div>
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-dantzOrange px-6 py-2.5 font-medium text-white transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzBlue"
        >
          {saving ? <SpinnerText>Saving…</SpinnerText> : "Save changes"}
        </button>
      </div>
    </form>
  )
}
